import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ERROR_MESSAGE } from '../constants/messages';

export type NotificationType = 'success' | 'error';

export interface NotificationItem {
  id: string;
  type: NotificationType;
  message: string;
}

interface NotificationContextType {
  notifications: NotificationItem[];
  showSuccess: (message: string) => void;
  showError: (message?: string) => void;
  dismissNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const AUTO_DISMISS_MS = 4000;

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const showNotification = useCallback(
    (type: NotificationType, message: string) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      setNotifications((prev) => [...prev, { id, type, message }]);

      window.setTimeout(() => {
        dismissNotification(id);
      }, AUTO_DISMISS_MS);
    },
    [dismissNotification]
  );

  const showSuccess = useCallback(
    (message: string) => {
      showNotification('success', message);
    },
    [showNotification]
  );

  const showError = useCallback(
    (message: string = ERROR_MESSAGE) => {
      showNotification('error', message);
    },
    [showNotification]
  );

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        showSuccess,
        showError,
        dismissNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
